import { ReactElement, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Button, ButtonProps } from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon, PlayIcon } from '@udacity/chakra-uds-icons';

import {
  SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE,
  SUBSCRIPTION_REFERRER_PROGRAM_TITLE,
  SUBSCRIPTION_REFERRER_URL_COOKIE,
} from '~/features/cookies/constants';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { setClientCookie } from '~/features/cookies/utils/set-client-cookie';
import { publicEnv } from '~/features/environment/public';
import { useFreeTrialState } from '~/features/free-trial/hooks/use-free-trial-state';
import { logger } from '~/features/logging/logger';
import { postEnrollment } from '~/features/program/controllers/post-enrollment';
import { SemanticType } from '~/features/program/models/program';
import { Enrollment } from '~/features/program/models/program-enrollment';
import { getClassroomUrl } from '~/features/program/utils/get-classroom-url';
import { pollForEnrollment } from '~/features/program/utils/poll-for-enrollment';
import { useUser } from '~/features/user/hooks/use-user';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

type CtaButtonWithHref = {
  text: string;
  icon?: ReactElement;
  href: string;
  shouldSaveReferrer: boolean;
  shouldCreateEnrollment: false;
};

type CtaButtonWithCreateEnrollment = {
  text: string;
  icon?: ReactElement;
  href?: never;
  shouldSaveReferrer: boolean;
  shouldCreateEnrollment: true;
};

type CtaButton = CtaButtonWithHref | CtaButtonWithCreateEnrollment;

export function ResumeOrSubscribeButton({
  enrollment,
  programKey,
  semanticType,
  isFree,
  inProgress,
  buttonProps,
  forcedButtonLabel,
  programTitle,
  forceUserToCheckout = false,
  programSlug,
  isLoading,
  onClick,
}: {
  enrollment?: Enrollment;
  programKey: string;
  semanticType: SemanticType;
  isFree: boolean;
  inProgress?: boolean;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  programTitle: string;
  forcedButtonLabel?: string | null;
  forceUserToCheckout?: boolean;
  programSlug?: string;
  isLoading?: boolean;
  onClick?: () => void;
}) {
  const { t, isRtl } = useI18n();
  const { userId } = useUser();
  const router = useRouter();
  const clientRender = useClientRender();
  const userHasAllAccess = getActiveSubscriptionCookie();
  const hasActiveEnrollment =
    enrollment?.state === 'ENROLLED' || enrollment?.state === 'STATIC_ACCESS' || enrollment?.state === 'GRADUATED';
  const isReadyForGraduation = enrollment?.isReadyForGraduation;
  const freeTrial = useFreeTrialState();
  const [isEnrolling, setIsEnrolling] = useState(false);

  const ctaButton = useMemo<CtaButton>(() => {
    if (clientRender && isReadyForGraduation) {
      return {
        text: t('program.graduate'),
        icon: <PlayIcon h='32px' w='32px' />,
        href: `${publicEnv.NEXT_PUBLIC_LEARN_URL}/graduation/${programKey}`,
        shouldSaveReferrer: false,
        shouldCreateEnrollment: false,
      };
    }
    // User has an active enrollment - link to Classroom
    else if (clientRender && hasActiveEnrollment) {
      return {
        text: inProgress ? t('program.resume') : t('program.startLearning'),
        href: `${enrollment.classroomLink}/resume-learning`,
        shouldSaveReferrer: false,
        shouldCreateEnrollment: false,
      };
    }
    // User is enrolled, and has an active subscription - link to Classroom
    else if (clientRender && enrollment && userHasAllAccess) {
      return {
        text: inProgress ? t('program.resume') : t('program.startLearning'),
        href: `${enrollment.classroomLink}/resume-learning`,
        shouldSaveReferrer: false,
        shouldCreateEnrollment: false,
      };
    }
    // User is enrolled, but does not have an active subscription, and the program is free - they can resume
    else if (clientRender && enrollment && isFree) {
      return {
        text: inProgress ? t('program.resume') : t('program.startLearning'),
        href: `${enrollment.classroomLink}/resume-learning`,
        shouldSaveReferrer: false,
        shouldCreateEnrollment: false,
      };
    }
    // User is enrolled, but does not have an active subscription, and the program requires an active subscription - link to Plans
    else if (clientRender && enrollment && !userHasAllAccess && !isFree) {
      return {
        text: t('program.subscribeToResume'),
        icon: isRtl ? <ArrowLeftIcon h='24px' w='24px' /> : <ArrowRightIcon h='24px' w='24px' />,
        href: forceUserToCheckout
          ? '/checkout/individual/monthly/credit-card'
          : router.asPath.startsWith('/enrollment')
            ? `/course/${programSlug}#plans`
            : '#plans',
        shouldSaveReferrer: true,
        shouldCreateEnrollment: false,
      };
    }
    // User is not enrolled, but has an active subscription - create enrollment and then link to Classroom
    else if (clientRender && !enrollment && userHasAllAccess) {
      return {
        text: t('program.enrollNow'),
        icon: <PlayIcon h='32px' w='32px' />,
        shouldSaveReferrer: false,
        shouldCreateEnrollment: true,
      };
    }
    // User is authenticated, and the program is free - create enrollment and then link to Classroom
    else if (clientRender && userId && isFree) {
      return {
        text: t('program.enrollNow'),
        icon: <PlayIcon h='32px' w='32px' />,
        shouldSaveReferrer: false,
        shouldCreateEnrollment: true,
      };
    }
    // User is not authenticated, and the program is free - link to Auth
    else if (clientRender && !userId && isFree) {
      return {
        text: t('program.enrollNow'),
        href: `${publicEnv.NEXT_PUBLIC_AUTH_URL}/sign-up?next=${window.location.href}`,
        icon: <PlayIcon h='32px' w='32px' />,
        shouldSaveReferrer: true,
        shouldCreateEnrollment: false,
      };
    }
    // User does not have an active subscription, and the program is not free - link to Plans
    else if (clientRender && !userHasAllAccess && !isFree) {
      return {
        text: t(freeTrial.status === 'on' ? 'program.startFreeTrial' : 'program.enrollNow'),
        icon: isRtl ? <ArrowLeftIcon h='24px' w='24px' /> : <ArrowRightIcon h='24px' w='24px' />,
        href: forceUserToCheckout ? '/checkout/individual/monthly/credit-card' : '#plans',
        shouldSaveReferrer: true,
        shouldCreateEnrollment: false,
      };
    }
    // Fallback - link to Plans
    else {
      // At build time it will always reach this (clientRender will be false), so we're only concerned when this is reached on the client
      if (clientRender) {
        logger.error('Missing logic for determining the CTA button.', {
          file: 'src/features/program/components/resume-or-subscribe-button.tsx',
          function: 'ctaButton',
          info: {
            userId,
            userHasAllAccess,
            userIsEnrolled: Boolean(enrollment),
            isFree,
          },
        });
      }
      return {
        text: t(freeTrial.status === 'on' ? 'program.startFreeTrial' : 'program.subscribeToLearn'),
        icon: isRtl ? <ArrowLeftIcon h='24px' w='24px' /> : <ArrowRightIcon h='24px' w='24px' />,
        href: forceUserToCheckout ? '/checkout/individual/monthly/credit-card' : '#plans',
        shouldSaveReferrer: true,
        shouldCreateEnrollment: false,
      };
    }
  }, [
    clientRender,
    isReadyForGraduation,
    hasActiveEnrollment,
    enrollment,
    userHasAllAccess,
    isFree,
    userId,
    t,
    programKey,
    inProgress,
    isRtl,
    forceUserToCheckout,
    router.asPath,
    programSlug,
    freeTrial.status,
  ]);

  async function handleCtaButtonClick() {
    onClick?.();
    if (ctaButton.shouldSaveReferrer) {
      setClientCookie(SUBSCRIPTION_REFERRER_URL_COOKIE, window.location.pathname, 1);
      setClientCookie(SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE, programKey, 1);
      setClientCookie(SUBSCRIPTION_REFERRER_PROGRAM_TITLE, programTitle, 1);
    }

    if (ctaButton.shouldCreateEnrollment) {
      setIsEnrolling(true);
      await postEnrollment(programKey);

      // Wait for the enrollment to propagate through the backend
      await pollForEnrollment({ programKey });
      router.push(getClassroomUrl(programKey, semanticType));
    }

    if (ctaButton.href) {
      router.push(ctaButton.href);
    }
  }

  return (
    <Button
      {...buttonProps}
      {...(isRtl
        ? {
            leftIcon: ctaButton.icon,
            size: 'icon-left',
          }
        : {
            rightIcon: ctaButton.icon,
            size: 'icon-right',
          })}
      backgroundColor='primary.blue'
      colorScheme='blue'
      isLoading={!clientRender || freeTrial.status === 'loading' || isLoading || isEnrolling}
      loadingText={t('common.loading')}
      variant='solid'
      width='100%'
      onClick={handleCtaButtonClick}
    >
      {forcedButtonLabel || ctaButton.text}
    </Button>
  );
}
